import { Box, Container, Stack, Switch, Typography } from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { useState } from "react";
import { useFragment, useLazyLoadQuery } from "react-relay";
import { AutomationTabQuery } from "./__generated__/AutomationTabQuery.graphql";
import { AutomationTab_brand$key } from "./__generated__/AutomationTab_brand.graphql";
import CardSection from "../../../components/CardSection";
import SaveButton, { SavedState } from "../../../components/SaveButton";
import useBrandAppConfigUpdate2Mutation from "../../../mutations/useBrandAppConfigUpdate2Mutation";
import AutomatedNotificationsForm from "./automation/notifications/AutomatedNotificationsForm";

const query = graphql`
  query AutomationTabQuery($id: ID!) {
    brand(id: $id) {
      ...AutomationTab_brand
    }
  }
`;

const brandFragment = graphql`
  fragment AutomationTab_brand on BrandType {
    appConfig {
      id
      postPurchaseOptInEnabled
      automatedNotifications {
        ...AutomatedNotificationsForm_automatedNotifications
      }
    }
  }
`;

const AutomatedUpsellSection = ({ brandID }: { brandID: string }) => {
  const data = useLazyLoadQuery<AutomationTabQuery>(query, {
    id: brandID,
  });
  const brand = useFragment<AutomationTab_brand$key>(brandFragment, data.brand);
  const appConfig = brand.appConfig;

  const [saveButtonState, setSaveButtonState] = useState<SavedState>(
    SavedState.DISABLED
  );
  const [saveMutation, isMutationInFlight] = useBrandAppConfigUpdate2Mutation();
  const [isPostPurchaseUpsellEnabled, setIsPostPurchaseUpsellEnabled] =
    useState(appConfig.postPurchaseOptInEnabled);
  const onSubmit = () => {
    saveMutation(
      {
        id: brand.appConfig.id,
        postPurchaseOptInEnabled: isPostPurchaseUpsellEnabled,
      },
      () => {
        setSaveButtonState(SavedState.SAVED);
      }
    );
  };
  return (
    <CardSection
      actions={
        <SaveButton
          savedState={isMutationInFlight ? SavedState.SAVING : saveButtonState}
          onClick={() => onSubmit()}
        />
      }
      title={"Upsell"}
      content={
        <Stack spacing={2} width="100%">
          <Box>
            <Typography variant="body2">
              Use the left toggle and save to enable or disable post-purchase
              notification opt-in upsell
            </Typography>
          </Box>
          <Box
            sx={{
              border: "1px solid #E0E0E0",
              borderRadius: "8px",
            }}
          >
            <Stack direction={"row"} alignItems={"center"}>
              <Switch
                checked={isPostPurchaseUpsellEnabled}
                onChange={(_, data) => {
                  setIsPostPurchaseUpsellEnabled(data);
                  setSaveButtonState(SavedState.ENABLED);
                }}
              />
              <Box
                sx={{
                  px: 2,
                  py: 1,
                }}
              >
                <Typography sx={{ paddingRight: "32px" }} variant="overline">
                  {"Post-purchase notification upsell"}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Stack>
      }
    />
  );
};

const AutomationTab = ({ brandID }: { brandID: string }) => {
  const data = useLazyLoadQuery<AutomationTabQuery>(query, {
    id: brandID,
  });
  const brand = useFragment<AutomationTab_brand$key>(brandFragment, data.brand);

  const [saveMutation, isMutationInFlight] = useBrandAppConfigUpdate2Mutation();

  return (
    <Container maxWidth="md">
      <AutomatedUpsellSection brandID={brandID} />
      <AutomatedNotificationsForm
        appConfigId={brand.appConfig.id}
        automatedNotifications={brand.appConfig.automatedNotifications}
        handleSave={(automatedNotifications, onCompleted) => {
          saveMutation(
            {
              id: brand.appConfig.id,
              automatedNotifications,
            },
            onCompleted
          );
        }}
        isSaving={isMutationInFlight}
      />
    </Container>
  );
};

export default AutomationTab;
